import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

const BUCKET = "avatars";
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED = ["image/png", "image/jpeg", "image/webp", "image/gif"];

/**
 * VIB-998: last opp / bytt profilbilde. Lagrer i den public avatars-bucketen
 * (unik sti per opplasting) og oppdaterer users.avatar_url. UI gater på canEdit
 * (admin eller seg selv); storage-policy tillater innloggede å skrive.
 */
export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, file }: { userId: string; file: File }): Promise<string> => {
      if (!ALLOWED.includes(file.type)) {
        throw new Error("Kun bilder (PNG, JPG, WebP eller GIF)");
      }
      if (file.size > MAX_BYTES) {
        throw new Error("Bildet er større enn 5 MB");
      }
      const ext = file.name.split(".").pop()?.toLowerCase() || "png";
      const path = `${userId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;

      const url = supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
      const { error: updErr } = await supabase
        .from("users")
        .update({ avatar_url: url })
        .eq("id", userId);
      if (updErr) throw updErr;
      return url;
    },
    onSuccess: async (_url, { userId }) => {
      await invalidateFor(qc, "users", { userId });
      toast.success("Profilbilde oppdatert");
    },
    onError: (err) => {
      toast.error(`Kunne ikke laste opp bilde: ${(err as Error).message}`);
    },
  });
}
